import { createContext } from 'react';

/**
 * Le contexte des onglets ouverts, et rien d'autre. Même raison que `lib/theme-context.ts` :
 * séparer l'identité du contexte du module qui contient le fournisseur et les hooks, sans quoi un
 * rechargement à chaud en crée une deuxième et les consommateurs ne trouvent plus leur fournisseur.
 *
 * Patients et consultations partagent cette barre. Une consultation n'est pas un second système :
 * c'est une entrée de plus, avec le même point d'alerte et le même dialogue de fermeture.
 */

export interface PatientTabInfo {
  kind: 'patient';
  id: string;
  firstName: string;
  lastName: string;
}

export interface ConsultationTabInfo {
  kind: 'consultation';
  id: string;
  /** Instant ISO du début de séance. `null` tant que la fiche n'est pas chargée. */
  startedAt: string | null;
  patientId: string;
  section: string;
  search: string;
}

export type OpenTab = PatientTabInfo | ConsultationTabInfo;

export interface DirtyRegion {
  key: string;
  label: string;
  canSave: boolean;
  canDiscard: boolean;
}

export interface DirtyTabSnapshot {
  patientId: string;
  patientName: string;
  regions: DirtyRegion[];
}

export interface RegionCallbacks {
  label: string;
  save?: () => Promise<void>;
  discard?: () => void;
}

export interface OpenConsultationInput {
  id: string;
  section: string;
  search: string;
  startedAt?: string | null;
  patientId?: string;
}

export interface PatientTabsValue {
  tabs: OpenTab[];
  activeTabId: string | null;
  openTab: (patient: { id: string; firstName: string; lastName: string }) => void;
  openConsultation: (tab: OpenConsultationInput) => void;
  updateConsultation: (id: string, patch: { startedAt: string; patientId: string }) => void;
  closeTab: (tabId: string, force?: boolean) => void;
  setActiveTab: (tabId: string) => void;
  isDirty: (tabId: string) => boolean;
  dirtyRegions: (tabId: string) => DirtyRegion[];
  dirtySnapshot: () => DirtyTabSnapshot[];
  saveRegion: (tabId: string, regionKey: string) => Promise<void>;
  discardRegion: (tabId: string, regionKey: string) => void;
  registerRegion: (tabId: string, regionKey: string, callbacks: RegionCallbacks) => () => void;
  /** Vrai si la navigation a été retenue : l'appelant ne doit pas naviguer. */
  requestNavigation: (to: string, commit: () => void) => boolean;
  pendingNavigation: string | null;
  confirmPendingNavigation: () => void;
  cancelPendingNavigation: () => void;
}

export const PatientTabsContext = createContext<PatientTabsValue | null>(null);
